import React, { useEffect, useRef } from 'react';
import { useTownState } from '../context/TownStateContext';

const ConversationLog: React.FC = () => {
  const { conversations } = useTownState();
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [conversations]);

  const formatTime = (date?: Date | string | number) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  };

  if (!conversations || conversations.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500 font-mono bg-[#2c2c54]">
        <p>No conversations yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full font-mono text-sm overflow-hidden bg-[#2c2c54]">
      {/* Minimal Header - seamless with panel */}
      <div className="p-2 text-center border-b border-[#4a4a7d] bg-[#2c2c54] shrink-0">
        <h3 className="text-xl font-bold text-[#ffaf40] tracking-wider mb-0" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
          Town Conversations
        </h3>
      </div>

      <div ref={logRef} className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar">
        {conversations.map((conversation, i) => (
          <div key={i} className="bg-[#1e1e2e] p-3 rounded shadow-md border border-[#3b3b5c]">
            {/* HEADER: Who & Time */}
            <div className="flex justify-between items-baseline mb-1 border-b border-[#3b3b5c] pb-1">
              <div className="text-[#a6accd] font-bold text-sm" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                {conversation.participants.join(' & ')}
              </div>
              <div className="text-xs text-[#6c7086] font-mono">
                {formatTime(conversation.timestamp)}
              </div>
            </div>

            {/* Location Line */}
            <div className="mb-3 text-xs text-[#6c7086]">
              at the <span className="text-[#9ece6a]">{conversation.location || 'Unknown'}</span>
            </div>

            {/* MESSAGES */}
            <div className="space-y-3 pl-1">
              {conversation.messages.slice(0, 6).map((message, j) => {
                const charName = message.speaker || message.characterId || 'Unknown';
                const text = typeof message.text === 'string' ? message.text : JSON.stringify(message);
                return (
                  <div key={j} className="leading-relaxed relative">
                    <span className="block text-white font-bold text-base mb-0.5" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                      {charName}:
                    </span>
                    <div className="relative pl-3 border-l-2 border-[#565f89]">
                      <span className="block text-[#e0e0e0] text-sm" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                        "{text}"
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {conversation.messages.length > 6 && (
              <div className="text-[10px] text-gray-500 italic mt-2 text-center">
                ... (+{conversation.messages.length - 6})
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationLog;
